import { useEffect, useState } from 'react'
import { Card } from '@/components/Card'
import { fetchApi } from '@/lib/api'
import {
  RefreshCw,
  ExternalLink,
  ChevronDown,
  ChevronRight,
  Search,
  AlertCircle,
  AlertTriangle,
  Info,
  Copy,
  Check,
} from 'lucide-react'
import { formatDateTime, timeAgo } from '@/lib/date'

type SentryProject = {
  id: string
  slug: string
  name: string
  platform: string | null
}

type SentryIssue = {
  id: string
  shortId: string
  title: string
  culprit: string
  level: string
  status: string
  platform: string | null
  project: string
  count: string
  userCount: number
  firstSeen: string
  lastSeen: string
  permalink: string
  metadata: Record<string, string>
}

type StackFrame = {
  filename: string | null
  function: string | null
  lineNo: number | null
  colNo: number | null
  context: [number, string][]
  inApp: boolean
}

type ExceptionValue = {
  type: string
  value: string
  frames: StackFrame[]
}

type SentryEvent = {
  eventID: string
  dateCreated: string
  title: string
  message: string
  platform: string | null
  tags: Record<string, string>
  exception: ExceptionValue[] | null
}

function levelIcon(level: string) {
  switch (level) {
    case 'error':
    case 'fatal':
      return <AlertCircle size={14} className="text-red-400" />
    case 'warning':
      return <AlertTriangle size={14} className="text-yellow-400" />
    default:
      return <Info size={14} className="text-blue-400" />
  }
}

function levelColor(level: string): string {
  switch (level) {
    case 'error':
    case 'fatal':
      return 'text-red-400'
    case 'warning':
      return 'text-yellow-400'
    default:
      return 'text-blue-400'
  }
}

function statusBadge(status: string) {
  const styles: Record<string, string> = {
    unresolved: 'bg-orange-500/10 text-orange-400',
    resolved: 'bg-green-500/10 text-green-400',
    ignored: 'bg-gray-500/10 text-gray-400',
  }
  return (
    <span className={`text-xs px-1.5 py-0.5 rounded ${styles[status] || styles.unresolved}`}>
      {status}
    </span>
  )
}

/** Build a plain-text summary of issue + events for clipboard copy. */
function buildCopyText(issue: SentryIssue, events: SentryEvent[]): string {
  const lines: string[] = []
  lines.push(`[${issue.shortId}] ${issue.title}`)
  lines.push(`Project: ${issue.project}`)
  lines.push(`Level: ${issue.level}  Status: ${issue.status}`)
  lines.push(`Culprit: ${issue.culprit}`)
  lines.push(`Events: ${issue.count}  Users: ${issue.userCount}`)
  lines.push(`First seen: ${formatDateTime(issue.firstSeen)}`)
  lines.push(`Last seen: ${formatDateTime(issue.lastSeen)}`)
  lines.push(`Sentry URL: ${issue.permalink}`)

  for (const event of events) {
    lines.push('')
    lines.push(`--- Event ${event.eventID.slice(0, 12)} (${formatDateTime(event.dateCreated)}) ---`)
    if (Object.keys(event.tags).length > 0) {
      lines.push(`Tags: ${Object.entries(event.tags).map(([k, v]) => `${k}=${v}`).join(', ')}`)
    }

    if (event.exception) {
      for (const exc of event.exception) {
        lines.push('')
        lines.push(`${exc.type}: ${exc.value}`)
        lines.push('Stack trace:')
        for (const frame of exc.frames) {
          if (!frame.filename) continue
          const marker = frame.inApp ? '>' : ' '
          const loc = frame.lineNo ? `:${frame.lineNo}` : ''
          lines.push(`  ${marker} ${frame.filename}${loc} in ${frame.function || '?'}`)
        }
      }
    } else {
      lines.push(event.message || event.title)
    }
  }

  return lines.join('\n')
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation()
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // fallback
    }
  }

  return (
    <button
      onClick={handleCopy}
      title="Copy details"
      className="flex items-center gap-1 px-2 py-1 text-xs rounded bg-[var(--color-bg-hover)] hover:bg-[var(--color-border)] text-[var(--color-text-muted)] hover:text-[var(--color-text)] transition-colors"
    >
      {copied ? <Check size={12} className="text-green-400" /> : <Copy size={12} />}
      {copied ? 'Copied' : 'Copy'}
    </button>
  )
}

export function Sentry() {
  const [projects, setProjects] = useState<SentryProject[]>([])
  const [selectedProject, setSelectedProject] = useState<string>('')
  const [issues, setIssues] = useState<SentryIssue[]>([])
  const [loading, setLoading] = useState(true)
  const [issuesLoading, setIssuesLoading] = useState(false)
  const [query, setQuery] = useState('is:unresolved')
  const [sort, setSort] = useState('date')
  const [expandedIssue, setExpandedIssue] = useState<string | null>(null)
  const [issueEvents, setIssueEvents] = useState<Record<string, SentryEvent[]>>({})
  const [eventsLoading, setEventsLoading] = useState<string | null>(null)
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  const [loadingMore, setLoadingMore] = useState(false)

  const loadProjects = async () => {
    setLoading(true)
    try {
      const data = await fetchApi<{ projects: SentryProject[] }>('/sentry/projects')
      setProjects(data.projects)
    } catch {
      setProjects([])
    } finally {
      setLoading(false)
    }
  }

  const loadIssues = async (append = false) => {
    if (append) {
      setLoadingMore(true)
    } else {
      setIssuesLoading(true)
      setNextCursor(null)
    }
    try {
      const params = new URLSearchParams({
        query,
        sort,
        limit: '25',
      })
      if (selectedProject) params.set('project', selectedProject)
      if (append && nextCursor) params.set('cursor', nextCursor)

      const data = await fetchApi<{ issues: SentryIssue[]; next_cursor: string | null }>(
        `/sentry/issues?${params}`
      )

      if (append) {
        setIssues((prev) => [...prev, ...data.issues])
      } else {
        setIssues(data.issues)
      }
      setNextCursor(data.next_cursor)
    } catch {
      if (!append) setIssues([])
    } finally {
      setIssuesLoading(false)
      setLoadingMore(false)
    }
  }

  const loadIssueEvents = async (issueId: string) => {
    // Toggle: close if already expanded
    if (expandedIssue === issueId) {
      setExpandedIssue(null)
      return
    }
    setExpandedIssue(issueId)
    // Always fetch fresh data (latest event with full stack trace)
    setEventsLoading(issueId)
    try {
      const data = await fetchApi<{ events: SentryEvent[] }>(
        `/sentry/issues/${issueId}/events?limit=1`
      )
      setIssueEvents((prev) => ({ ...prev, [issueId]: data.events }))
    } catch {
      setIssueEvents((prev) => ({ ...prev, [issueId]: [] }))
    } finally {
      setEventsLoading(null)
    }
  }

  useEffect(() => {
    loadProjects()
  }, [])

  useEffect(() => {
    loadIssues()
  }, [selectedProject, sort])

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    loadIssues()
  }

  const isLoading = loading || issuesLoading

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold">Sentry</h2>
        <div className="flex items-center gap-2">
          <select
            value={selectedProject}
            onChange={(e) => setSelectedProject(e.target.value)}
            className="px-3 py-1.5 text-sm rounded border border-[var(--color-border)] bg-[var(--color-bg-card)] text-[var(--color-text)]"
          >
            <option value="">All Projects</option>
            {projects.map((p) => (
              <option key={p.slug} value={p.slug}>
                {p.name}
              </option>
            ))}
          </select>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="px-3 py-1.5 text-sm rounded border border-[var(--color-border)] bg-[var(--color-bg-card)] text-[var(--color-text)]"
          >
            <option value="date">Sort: Date</option>
            <option value="priority">Sort: Priority</option>
            <option value="events">Sort: Events</option>
            <option value="users">Sort: Users</option>
          </select>
          <button
            onClick={() => loadIssues()}
            disabled={isLoading}
            className="flex items-center gap-1 px-3 py-1.5 text-sm rounded bg-[var(--color-bg-hover)] hover:bg-[var(--color-border)] transition-colors"
          >
            <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Search bar */}
      <form onSubmit={handleSearch} className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-text-muted)]"
          />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Sentry query (e.g. is:unresolved level:error TypeError)"
            className="w-full pl-9 pr-3 py-2 text-sm rounded border border-[var(--color-border)] bg-[var(--color-bg-card)] text-[var(--color-text)] placeholder:text-[var(--color-text-muted)]"
          />
        </div>
        <button
          type="submit"
          disabled={issuesLoading}
          className="flex items-center gap-1 px-4 py-2 text-sm rounded bg-blue-600 hover:bg-blue-700 text-white transition-colors disabled:opacity-50"
        >
          {issuesLoading ? <RefreshCw size={14} className="animate-spin" /> : 'Search'}
        </button>
      </form>

      {/* Issues list */}
      <Card
        title={`Issues${selectedProject ? ` - ${selectedProject}` : ''} (${issues.length}${nextCursor ? '+' : ''})`}
      >
        {issuesLoading && issues.length === 0 ? (
          <p className="text-sm text-[var(--color-text-muted)]">Loading...</p>
        ) : issues.length > 0 ? (
          <div className="space-y-1">
            {issues.map((issue) => (
              <div key={issue.id}>
                <div
                  className="flex items-center justify-between p-3 rounded bg-[var(--color-bg)] cursor-pointer hover:bg-[var(--color-bg-hover)] transition-colors"
                  onClick={() => loadIssueEvents(issue.id)}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      {expandedIssue === issue.id ? (
                        <ChevronDown size={14} />
                      ) : (
                        <ChevronRight size={14} />
                      )}
                      {levelIcon(issue.level)}
                      <span className="text-sm font-medium truncate">{issue.title}</span>
                    </div>
                    <div className="flex items-center gap-3 mt-1 ml-5 text-xs text-[var(--color-text-muted)]">
                      {issue.project && (
                        <span className="px-1.5 py-0.5 rounded bg-[var(--color-bg-hover)] font-mono">
                          {issue.project}
                        </span>
                      )}
                      <span className="truncate">{issue.culprit}</span>
                      <span className="font-mono">{issue.shortId}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 ml-4 shrink-0">
                    <div className="text-right text-xs">
                      <div className="flex items-center gap-2">
                        <span className={`font-mono ${levelColor(issue.level)}`}>
                          {issue.count} events
                        </span>
                        <span className="text-[var(--color-text-muted)]">
                          {issue.userCount} users
                        </span>
                      </div>
                      <div className="text-[var(--color-text-muted)] mt-0.5">
                        {timeAgo(issue.lastSeen)}
                      </div>
                    </div>
                    {statusBadge(issue.status)}
                    <a
                      href={issue.permalink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <ExternalLink size={14} />
                    </a>
                  </div>
                </div>

                {/* Expanded: event details + stack trace */}
                {expandedIssue === issue.id && (
                  <div className="mx-3 mb-2 p-3 rounded bg-[var(--color-bg)] border border-[var(--color-border)] space-y-3">
                    {eventsLoading === issue.id ? (
                      <p className="text-xs text-[var(--color-text-muted)]">
                        Loading event details...
                      </p>
                    ) : (issueEvents[issue.id]?.length ?? 0) > 0 ? (
                      <>
                        {/* Copy button */}
                        <div className="flex justify-end">
                          <CopyButton
                            text={buildCopyText(issue, issueEvents[issue.id])}
                          />
                        </div>

                        {issueEvents[issue.id].map((event) => (
                          <div key={event.eventID} className="space-y-2">
                            {/* Event header */}
                            <div className="flex items-center justify-between text-xs">
                              <span className="font-mono text-[var(--color-text-muted)]">
                                Event {event.eventID.slice(0, 12)}
                              </span>
                              <span className="text-[var(--color-text-muted)]">
                                {formatDateTime(event.dateCreated)}
                              </span>
                            </div>

                            {/* Tags */}
                            {Object.keys(event.tags).length > 0 && (
                              <div className="flex flex-wrap gap-1">
                                {Object.entries(event.tags).map(([key, value]) => (
                                  <span
                                    key={key}
                                    className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--color-bg-hover)] text-[var(--color-text-muted)]"
                                  >
                                    {key}: {value}
                                  </span>
                                ))}
                              </div>
                            )}

                            {/* Stack trace */}
                            {event.exception && event.exception.length > 0 && (
                              <div className="space-y-3">
                                {event.exception.map((exc, i) => (
                                  <div key={i}>
                                    <div className="text-xs font-medium text-red-400 mb-1">
                                      {exc.type}: {exc.value}
                                    </div>
                                    {exc.frames.length > 0 && (
                                      <div className="max-h-72 overflow-auto rounded bg-[var(--color-bg-card)] p-2 border border-[var(--color-border)]">
                                        <div className="text-[11px] font-mono space-y-px">
                                          {exc.frames
                                            .filter((f) => f.filename)
                                            .map((frame, j) => (
                                              <div
                                                key={j}
                                                className={
                                                  frame.inApp
                                                    ? 'text-[var(--color-text)] bg-blue-500/10 px-2 py-0.5 rounded border-l-2 border-blue-500'
                                                    : 'text-[var(--color-text-muted)] px-2 py-0.5'
                                                }
                                              >
                                                <span className={frame.inApp ? 'text-blue-400' : 'text-[var(--color-text-muted)]'}>
                                                  {frame.filename}
                                                  {frame.lineNo ? `:${frame.lineNo}` : ''}
                                                </span>
                                                {' '}
                                                <span className="text-[var(--color-text-muted)]">in</span>
                                                {' '}
                                                <span className={frame.inApp ? 'text-yellow-300' : ''}>
                                                  {frame.function || '?'}
                                                </span>
                                              </div>
                                            ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}

                            {!event.exception && (
                              <div className="text-xs text-[var(--color-text-muted)] p-2 rounded bg-[var(--color-bg-card)]">
                                {event.message}
                              </div>
                            )}
                          </div>
                        ))}
                      </>
                    ) : (
                      <p className="text-xs text-[var(--color-text-muted)]">
                        No events available
                      </p>
                    )}

                    {/* Issue metadata */}
                    <div className="border-t border-[var(--color-border)] pt-2 grid grid-cols-2 gap-x-6 gap-y-1 text-xs">
                      <div>
                        <span className="text-[var(--color-text-muted)]">First seen: </span>
                        <span>{formatDateTime(issue.firstSeen)}</span>
                      </div>
                      <div>
                        <span className="text-[var(--color-text-muted)]">Last seen: </span>
                        <span>{formatDateTime(issue.lastSeen)}</span>
                      </div>
                      <div>
                        <span className="text-[var(--color-text-muted)]">Platform: </span>
                        <span>{issue.platform || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="text-[var(--color-text-muted)]">Level: </span>
                        <span className={levelColor(issue.level)}>{issue.level}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}

            {/* Load more */}
            {nextCursor && (
              <div className="pt-2 text-center">
                <button
                  onClick={() => loadIssues(true)}
                  disabled={loadingMore}
                  className="px-4 py-2 text-sm rounded bg-[var(--color-bg-hover)] hover:bg-[var(--color-border)] transition-colors disabled:opacity-50"
                >
                  {loadingMore ? (
                    <RefreshCw size={14} className="inline animate-spin mr-1" />
                  ) : null}
                  Load More
                </button>
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-[var(--color-text-muted)]">
            No issues found. Try adjusting the search query.
          </p>
        )}
      </Card>
    </div>
  )
}
